import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { formatCurrency } from '@/lib/utils';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { EstimatePricingChanges, EstimatePricingChange } from '@/hooks/useEstimatePricingRefresh';
import { useEstimateVersioningEnhanced } from './hooks/useEstimateVersioningEnhanced';
import { toast } from 'sonner';

interface PricingChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  estimateId: string;
  versionId?: string;
  versionStatus: string;
  versionType?: string;
  pricingChanges: EstimatePricingChanges;
  onVersionCreated: (versionId: string) => void;
}

const PricingChangesModal: React.FC<PricingChangesModalProps> = ({
  isOpen,
  onClose,
  estimateId,
  versionId,
  versionStatus,
  versionType,
  pricingChanges,
  onVersionCreated
}) => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isCreating, setIsCreating] = useState(false);
  const { createVersionEnhanced } = useEstimateVersioningEnhanced();

  // Calculate totals for selected items only
  const selectedChangesTotals = useMemo(() => {
    const selectedChanges = pricingChanges.changes.filter(change => 
      selectedItems.has(change.id)
    );
    
    const totalOldAmount = selectedChanges.reduce((sum, change) => sum + change.oldTotal, 0);
    const totalNewAmount = selectedChanges.reduce((sum, change) => sum + change.newTotal, 0);
    const totalChangeValue = totalNewAmount - totalOldAmount;
    
    return {
      selectedChanges,
      totalOldAmount,
      totalNewAmount,
      totalChangeValue,
      count: selectedChanges.length
    };
  }, [selectedItems, pricingChanges.changes]);

  const handleSelectAll = () => {
    if (selectedItems.size === pricingChanges.changes.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(pricingChanges.changes.map(change => change.id)));
    }
  };

  const handleSelectByTrade = (trade: string) => {
    const tradeChanges = pricingChanges.changesByTrade[trade] || [];
    const tradeIds = tradeChanges.map(change => change.id);
    const newSelected = new Set(selectedItems);
    
    if (tradeIds.every(id => selectedItems.has(id))) {
      // Deselect all in this trade
      tradeIds.forEach(id => newSelected.delete(id));
    } else {
      // Select all in this trade
      tradeIds.forEach(id => newSelected.add(id));
    }
    
    setSelectedItems(newSelected);
  };

  const handleSelectByCategory = (category: string) => {
    const categoryChanges = pricingChanges.changesByCategory[category] || [];
    const categoryIds = categoryChanges.map(change => change.id);
    const newSelected = new Set(selectedItems);
    
    if (categoryIds.every(id => selectedItems.has(id))) {
      // Deselect all in this category
      categoryIds.forEach(id => newSelected.delete(id));
    } else {
      // Select all in this category
      categoryIds.forEach(id => newSelected.add(id));
    }
    
    setSelectedItems(newSelected);
  };

  const handleItemToggle = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (selectedItems.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleUpdatePricing = async () => {
    if (selectedChangesTotals.count === 0) {
      toast.error('Please select at least one item to update');
      return;
    }

    setIsCreating(true);
    try {
      // Determine version type based on current status
      const newVersionType = versionStatus === 'approved' ? 'change_order' : 'estimate';
      
      // Create new version with updated pricing
      const result = await createVersionEnhanced(
        estimateId,
        selectedChangesTotals.selectedChanges,
        'Pricing refresh',
        `Updated pricing for ${selectedChangesTotals.count} line items`
      );

      // TODO: Update the specific line items in the new version with new pricing
      // This would require a new database function to selectively update pricing

      toast.success(`${result.version_type === 'change_order' ? 'Change order' : 'Version'} created successfully`);
      onVersionCreated(result.version_id);
      
    } catch (error) {
      console.error('Error creating version:', error);
      toast.error('Failed to create new version');
    } finally {
      setIsCreating(false);
    }
  };

  const renderPricingChange = (change: EstimatePricingChange) => {
    const isSelected = selectedItems.has(change.id);
    const isIncrease = change.changeValue > 0;
    
    return (
      <div key={change.id} className="flex items-center space-x-3 p-4 border rounded-lg">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => handleItemToggle(change.id)}
        />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm truncate">{change.description}</p>
              <div className="flex items-center gap-2 mt-1">
                {change.trade && (
                  <Badge variant="outline" className="text-xs">{change.trade}</Badge>
                )}
                {change.category && (
                  <Badge variant="secondary" className="text-xs">{change.category}</Badge>
                )}
              </div>
            </div>
            
            <div className="text-right">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground line-through">
                  {formatCurrency(change.oldPrice)}
                </span>
                <span className="text-sm font-medium">
                  {formatCurrency(change.newPrice)}
                </span>
                {isIncrease ? (
                  <TrendingUp className="h-4 w-4 text-red-500" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-green-500" />
                )}
              </div>
              
              <div className="text-xs text-muted-foreground mt-1">
                {isIncrease ? '+' : ''}{formatCurrency(change.changeValue)} 
                ({isIncrease ? '+' : ''}{change.changePercentage.toFixed(2)}%)
              </div>
              
              <div className="text-xs text-muted-foreground">
                Total: {formatCurrency(change.oldTotal)} → {formatCurrency(change.newTotal)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Pricing Changes Detected
          </DialogTitle>
          <DialogDescription>
            {pricingChanges.changes.length} line items have pricing changes. 
            Select which items to update and create a new {versionStatus === 'approved' ? 'change order' : 'version'}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {/* Summary Card */}
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-sm">Impact Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Total Changes</p>
                  <p className="font-medium">{pricingChanges.changes.length} items</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Selected Changes</p>
                  <p className="font-medium">{selectedChangesTotals.count} items</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Selected Impact</p>
                  <p className={`font-medium ${selectedChangesTotals.totalChangeValue >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {selectedChangesTotals.totalChangeValue >= 0 ? '+' : ''}{formatCurrency(selectedChangesTotals.totalChangeValue)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selection Controls */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedItems.size === pricingChanges.changes.length ? 'Deselect All' : 'Select All'}
            </Button>
            
            {Object.keys(pricingChanges.changesByTrade).map(trade => (
              <Button
                key={trade}
                variant="outline"
                size="sm"
                onClick={() => handleSelectByTrade(trade)}
              >
                {trade} ({pricingChanges.changesByTrade[trade].length})
              </Button>
            ))}
            
            {Object.keys(pricingChanges.changesByCategory).map(category => (
              <Button
                key={category}
                variant="outline"
                size="sm"
                onClick={() => handleSelectByCategory(category)}
              >
                {category} ({pricingChanges.changesByCategory[category].length})
              </Button>
            ))}
          </div>

          <Tabs defaultValue="all" className="w-full">
            <TabsList>
              <TabsTrigger value="all">All Changes ({pricingChanges.changes.length})</TabsTrigger>
              <TabsTrigger value="by-trade">By Trade</TabsTrigger>
              <TabsTrigger value="by-category">By Category</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="space-y-2 max-h-96 overflow-auto">
              {pricingChanges.changes.map(renderPricingChange)}
            </TabsContent>
            
            <TabsContent value="by-trade" className="space-y-4 max-h-96 overflow-auto">
              {Object.entries(pricingChanges.changesByTrade).map(([trade, changes]) => (
                <div key={trade}>
                  <h4 className="font-medium mb-2">{trade} ({changes.length} items)</h4>
                  <div className="space-y-2 ml-4">
                    {changes.map(renderPricingChange)}
                  </div>
                </div>
              ))}
            </TabsContent>
            
            <TabsContent value="by-category" className="space-y-4 max-h-96 overflow-auto">
              {Object.entries(pricingChanges.changesByCategory).map(([category, changes]) => (
                <div key={category}>
                  <h4 className="font-medium mb-2">{category} ({changes.length} items)</h4>
                  <div className="space-y-2 ml-4">
                    {changes.map(renderPricingChange)}
                  </div>
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </div>

        <Separator />
        
        <div className="flex justify-between items-center pt-4">
          <div className="text-sm text-muted-foreground">
            Creating {versionStatus === 'approved' ? 'change order' : 'new version'} with {selectedChangesTotals.count} updated items
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdatePricing}
              disabled={selectedChangesTotals.count === 0 || isCreating}
            >
              {isCreating ? 'Creating...' : `Update ${selectedChangesTotals.count} Items`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PricingChangesModal;